import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Rating } from "react-simple-star-rating";

import {
  useRTCClient,
  useIsConnected,
  useClientEvent,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  useRemoteUsers,
  useJoin,
  usePublish,
  LocalUser,
  RemoteUser,
} from "agora-rtc-react";

import { ref, update, onValue, off, serverTimestamp } from "firebase/database";
import { firebase_db } from "../../FirebaseSetup";

import ApiService from "../../services/ApiService";
import { authActions } from "../../redux/auth";

import Swal from "sweetalert2";
import { toast } from "react-toastify";

import "./Call.css";

function PrivateVideoRoomScreen() {
  // -----------------------------------------------------------------
  // Router / Redux helpers
  // -----------------------------------------------------------------
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId } = useSelector((state) => state.auth);

  // -----------------------------------------------------------------
  // Session data (coming from previous page via location.state)
  // -----------------------------------------------------------------
  const session = useMemo(() => location.state || {}, [location.state]);

  // -----------------------------------------------------------------
  // 1️⃣ Stable channel name – never changes while component lives
  // -----------------------------------------------------------------
  const channelNameRef = useRef(
    session.channelName || `private_${Date.now()}`
  );
  const channelName = channelNameRef.current;

  // Debug – prints once per mount
  useEffect(() => {
    console.log("🚀 Agora channelName (stable):", channelName);
  }, [channelName]);

  // -----------------------------------------------------------------
  // 2️⃣ Determine Agora client role (host ↔ audience)
  // -----------------------------------------------------------------
  // For a 1‑to‑1 call both participants must be able to publish video/audio.
  // Therefore we force the Agora client role to "host" irrespective of the
  // original session.role. This eliminates the case where the remote user is
  // classified as "audience" and cannot publish its stream.
  const role = session.role || "host";

  // -----------------------------------------------------------------
  // UI helpers (peer name / avatar)
  // -----------------------------------------------------------------
  const peerName = useMemo(
    () => (role === "host" ? session.sender_name : session.receiver_name),
    [role, session.sender_name, session.receiver_name]
  );

  const peerImage = useMemo(
    () =>
      role === "host"
        ? "/images/step/01.png"
        : session.receiver_image || "/images/step/01.png",
    [role, session.receiver_image]
  );

  // -----------------------------------------------------------------
  // Timer / clock helpers (optional – keep your original logic)
  // -----------------------------------------------------------------
  const totalDurationSeconds = useMemo(
    () => Number(session.totalSeconds) || 900,
    [session.totalSeconds]
  );

  const [joined, setJoined] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [audioMuted, setAudioMuted] = useState(false);
  const [videoMuted, setVideoMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [readyToJoin, setReadyToJoin] = useState(false);

  // Rating and review states
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingVal, setRatingVal] = useState(0);
  const [commentVal, setCommentVal] = useState("");

  // -----------------------------------------------------------------
  // Delay joining / track acquisition to avoid device conflict/race conditions
  // -----------------------------------------------------------------
  useEffect(() => {
    const timer = setTimeout(() => {
      setReadyToJoin(true);
    }, 1200); // 1.2s delay to guarantee clean teardown of previous live stream
    return () => clearTimeout(timer);
  }, []);

  // -----------------------------------------------------------------
  // Agora SDK primitives
  // -----------------------------------------------------------------
  const agoraEngine = useRTCClient();

  const { localCameraTrack } = useLocalCameraTrack(readyToJoin);
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(readyToJoin);
  const isConnected = useIsConnected();
  const remoteUsers = useRemoteUsers(); // [{uid, videoTrack, audioTrack, …}]

  // -----------------------------------------------------------------
  // Debug: log local tracks availability
  // -----------------------------------------------------------------
  useEffect(() => {
    console.log('🔧 Local camera track available?', !!localCameraTrack);
    console.log('🔧 Local microphone track available?', !!localMicrophoneTrack);
  }, [localCameraTrack, localMicrophoneTrack]);
  // -----------------------------------------------------------------
  // Debug: log remote users and their tracks
  // -----------------------------------------------------------------
  useEffect(() => {
    console.log('🟢 Remote users count:', remoteUsers.length);
    remoteUsers.forEach((u) => {
      console.log('👤 Remote uid:', u.uid, 'videoTrack?', !!u.videoTrack, 'audioTrack?', !!u.audioTrack);
    });
  }, [remoteUsers]);

  // -----------------------------------------------------------------
  // 3️⃣ Join the channel – runs once (channelName is stable)
  // -----------------------------------------------------------------
  useJoin({
    appid: process.env.REACT_APP_AGORA_APP_ID,
    channel: channelName,
    token: null,
    role: "host",
  }, readyToJoin);

  // Enable audio & video once the client has joined the channel
  useEffect(() => {
    if (joined) {
      agoraEngine?.enableVideo?.();
      agoraEngine?.enableAudio?.();
      console.log('🔊 Audio & 🎥 Video enabled');
    }
  }, [joined]);

  // -----------------------------------------------------------------
  // 4️⃣ Publish local tracks only after they are ready and after join
  // -----------------------------------------------------------------
  const tracksReady = !!localCameraTrack && !!localMicrophoneTrack;
  usePublish(
    [localCameraTrack, localMicrophoneTrack].filter(Boolean),
    tracksReady
  );

  // -----------------------------------------------------------------
  // 5️⃣ Event listeners – debug logs for every important lifecycle event
  // -----------------------------------------------------------------
  useClientEvent(agoraEngine, "join-channel-success", async () => {
    console.log("✅ Joined Agora channel:", channelName);
    setJoined(true);
    // optional backend notification
    if (session.vip_room_db_request_id) {
      try {
        await new ApiService().POST("/private-session-join", {
          request_id: session.vip_room_db_request_id,
          time: Date.now(),
        });
      } catch (err) {
        console.error("⚡️ Backend join error:", err);
      }
    }
  });

  // Track active remote users in this channel session
  const activeRemoteUidsRef = useRef(new Set());

  useClientEvent(agoraEngine, "user-published", async (user, mediaType) => {
    console.log(
      `🚀 Remote user ${user.uid} published ${mediaType} – subscribing…`
    );
    activeRemoteUidsRef.current.add(String(user.uid));
    try {
      await agoraEngine.subscribe(user, mediaType);
      console.log(`✅ Subscribed to ${mediaType} of ${user.uid}`);
    } catch (err) {
      console.error(`❌ Subscribe failed for ${user.uid}:`, err);
    }
  });

  useClientEvent(agoraEngine, "user-left", (user) => {
    const uidStr = String(user.uid);
    if (!activeRemoteUidsRef.current.has(uidStr)) {
      console.log(`ℹ️ Ignored stale user-left event for uid ${user.uid}`);
      return;
    }
    console.warn(`⚠️ Remote user ${user.uid} left`);
    toast.info(`${peerName} left the call.`);
    handleCallEnd();
  });

  useClientEvent(agoraEngine, "connection-state-changed", (newState) => {
    console.log("📡 Agora connection state:", newState);
  });

  // -----------------------------------------------------------------
  // 6️⃣ Firebase listener for call status (complete / cancel)
  // -----------------------------------------------------------------
  useEffect(() => {
    if (!channelName) return;
    const callStatusRef = ref(
      firebase_db,
      `/user_video_audio_call_requests/${channelName}`
    );
    const offListener = onValue(callStatusRef, (snap) => {
      const val = snap.val();
      if (val?.status === "complete" && !finalizingRef.current) {
        toast.info("VIP call marked complete – wrapping up.");
        handleCallEnd();
      }
    });
    return () => off(callStatusRef, "value", offListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelName]);

  // -----------------------------------------------------------------
  // 7️⃣ Misc helpers – timers, mute toggles, end‑call flow
  // -----------------------------------------------------------------
  const callStartRef = useRef(Date.now());
  const finalizingRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setElapsedSeconds(Math.floor((now - callStartRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const toggleMuteAudio = () => setAudioMuted((p) => !p);
  const toggleMuteVideo = () => setVideoMuted((p) => !p);

  const handleCallEnd = async () => {
    if (finalizingRef.current) return;
    finalizingRef.current = true;
    setIsLoading(true);

    const seconds = Math.max(0, Math.floor((Date.now() - callStartRef.current) / 1000));
    const isSender = String(session.sender_id) === String(userId);
    const peerName = isSender ? session.receiver_name : session.sender_name;

    if (session?.vip_room_db_request_id) {
      try {
        const res = await new ApiService().POST("/private-session-join-end", {
          request_id: session.vip_room_db_request_id,
          user_id: session.sender_id,
          time: Date.now(),
          call_duration: seconds,
        });

        console.log("Private session end API response:", res.data);
        const deductAmount = Number(res.data?.balance_deduct || 0).toFixed(2);

        // Build end message
        const durationHrs = Math.floor(seconds / 3600);
        const durationMins = Math.floor((seconds % 3600) / 60);
        const durationSecs = seconds % 60;
        const durationStr = [durationHrs, durationMins, durationSecs]
          .map((value) => String(value).padStart(2, "0"))
          .join(":");

        let endMessage = `VIP Room Session with ${peerName} has ended. Duration: ${durationStr}`;
        if (isSender) {
          endMessage += `. $${deductAmount} has been deducted from your Wallet.`;
        }

        Swal.fire({
          title: "VIP Room Ended",
          text: endMessage,
          icon: "info",
          confirmButtonColor: "#8abb45",
        });
      } catch (err) {
        console.error("Private session end API error:", err);
      }
    }

    try {
      const updates = {};
      updates[`/user_video_audio_call_requests/${channelName}/status`] = "complete";
      updates[`/user_video_audio_call_requests/${channelName}/created_at`] = serverTimestamp();
      await update(ref(firebase_db), updates);
    } catch (err) {
      console.error("update call status error", err);
    }

    localCameraTrack?.close?.();
    localMicrophoneTrack?.close?.();

    setIsLoading(false);

    if (role !== "host") {
      setShowRatingModal(true);
    } else {
      navigate("/find-match", { replace: true });
    }
  };

  const submitRating = async () => {
    if (ratingVal === 0) {
      toast.warning("Please select a star rating first.");
      return;
    }
    setIsLoading(true);

    let new_data = {
      user_id: userId,
      request_id: session.vip_room_db_request_id,
      rating: ratingVal,
      feedback_msg: commentVal,
    };

    try {
      var responce = await new ApiService().POST(`/private-stream-rating`, new_data);
      if (responce.data.status === "1") {
        toast.success("Thank you for your feedback!");
      } else {
        toast.warning(responce.data.message || "Feedback not saved.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit feedback.");
    } finally {
      setIsLoading(false);
      setShowRatingModal(false);
      navigate("/find-match", { replace: true });
    }
  };

  const skipRating = () => {
    setShowRatingModal(false);
    navigate("/find-match", { replace: true });
  };

  // -----------------------------------------------------------------
  // 8️⃣ Cleanup – leave channel & close tracks on component unmount
  // -----------------------------------------------------------------
  useEffect(() => {
    return async () => {
      try {
        if (agoraEngine) {
          await agoraEngine.leave?.();
        }
        localCameraTrack?.close?.();
        localMicrophoneTrack?.close?.();
        console.log("🧹 Live stream left and tracks closed before private call");
      } catch (e) {
        console.error("⚠️ Error leaving live stream:", e);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------------------------------------------
  // 9️⃣ Render – clear decision tree, responsive layout
  // -----------------------------------------------------------------
  const remainingSeconds = Math.max(0, totalDurationSeconds - elapsedSeconds);
  const remHours = Math.floor(remainingSeconds / 3600);
  const remMinutes = Math.floor((remainingSeconds % 3600) / 60);
  const remSecs = remainingSeconds % 60;

  return (
    <div className="call-screen-wrapper">
      {/* Timer badge */}
      <div className="timer-badge">
        <div className="pulse-icon" />
        <span>
          {String(remHours).padStart(2, "0")}: {String(remMinutes).padStart(2, "0")}: {String(remSecs).padStart(2, "0")}
        </span>
      </div>

      {/* Main video stage */}
      <div className="video-stage">
        {remoteUsers.length > 0 ? (
          remoteUsers
            .filter((u) => u.videoTrack)
            .map((user) => (
              <RemoteUser
                key={user.uid}
                user={user}
                videoTrack={user.videoTrack}
                audioTrack={user.audioTrack}
                playVideo={true}
                playAudio={true}
                className="agora-video-view"
              />
            ))
        ) : role === "host" ? (
          <LocalUser
            videoTrack={localCameraTrack}
            audioTrack={localMicrophoneTrack}
            cameraOn={!videoMuted}
            micOn={!audioMuted}
            playVideo={true}
            playAudio={false}
            className="agora-video-view"
          />
        ) : (
          <div className="viewport-loading">
            <img
              src={peerImage}
              style={{
                width: "110px",
                height: "110px",
                borderRadius: "50%",
                border: "3px solid #8abb45",
                boxShadow: "0 0 15px rgba(138, 187, 69, 0.4)",
              }}
              alt=""
            />
            <h5 style={{ marginTop: "20px", fontWeight: 700 }}>
              Connecting VIP Call
            </h5>
            <p>Waiting for {peerName} to establish video tracks...</p>
          </div>
        )}
      </div>

      {/* Small floating PIP – shows loading until tracks are ready */}
      {isConnected && (localCameraTrack || localMicrophoneTrack) ? (
        <div className="local-pip-wrapper">
          <LocalUser
            videoTrack={localCameraTrack}
            audioTrack={localMicrophoneTrack}
            cameraOn={!videoMuted}
            micOn={!audioMuted}
            playVideo={true}
            playAudio={false}
            className="agora-video-view"
          />
          <div className="drag-tip">Me</div>
        </div>
      ) : (
        <div className="local-pip-wrapper loading">
          <div className="spinner-border text-success" role="status" />
          <p>Loading camera…</p>
        </div>
      )}

      {/* Bottom control bar */}
      <div className="controls-bar">
        {/* Mic toggle */}
        <div className="control-item-wrap">
          <button
            className={`control-circle-btn ${audioMuted ? "" : "active"}`}
            onClick={toggleMuteAudio}
          >
            <i
              className={`fa-solid ${audioMuted ? "fa-microphone-slash" : "fa-microphone"}`}
            />
          </button>
          <span className="control-btn-label">Mic</span>
        </div>

        {/* End call */}
        <div className="control-item-wrap">
          <button className="control-circle-btn danger" onClick={handleCallEnd}>
            <i className="fa-solid fa-phone-slash" />
          </button>
          <span className="control-btn-label">End Call</span>
        </div>

        {/* Camera toggle */}
        <div className="control-item-wrap">
          <button
            className={`control-circle-btn ${videoMuted ? "" : "active"}`}
            onClick={toggleMuteVideo}
          >
            <i
              className={`fa-solid ${videoMuted ? "fa-video-slash" : "fa-video"}`}
            />
          </button>
          <span className="control-btn-label">Cam</span>
        </div>
      </div>

      {/* Loading overlay (optional) */}
      {isLoading && (
        <div
          className="viewport-loading"
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(15, 18, 24, 0.8)",
            zIndex: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
          }}
        >
          <div className="spinner-border text-success" role="status" />
          <p>Finalizing VIP private room transaction…</p>
        </div>
      )}

      {/* 🌟 Premium Feedback & Rating Modal Overlay */}
      {showRatingModal && (
        <div
          className="viewport-loading"
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(15, 18, 24, 0.95)",
            zIndex: 30,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            padding: "20px",
          }}
        >
          <div
            className="feedback-card"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "20px",
              padding: "40px",
              width: "100%",
              maxWidth: "400px",
              textAlign: "center",
              backdropFilter: "blur(20px)",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.5)",
            }}
          >
            <div
              style={{
                width: "120px",
                height: "120px",
                borderRadius: "50%",
                border: "5px solid #8abb45",
                boxShadow: "0 0 20px rgba(138, 187, 69, 0.4)",
                overflow: "hidden",
                margin: "0 auto 20px",
              }}
            >
              <img
                src={peerImage}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                alt=""
              />
            </div>

            <h3 style={{ fontSize: "22px", fontWeight: 700, margin: "10px 0" }}>
              {peerName}
            </h3>

            <p style={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "16px", marginBottom: "20px" }}>
              Give Your Feedback
            </p>

            <div style={{ marginBottom: "25px" }}>
              <Rating
                onClick={(val) => setRatingVal(val)}
                initialValue={ratingVal}
                size={35}
                fillColor="#ffca28"
                emptyColor="rgba(255, 255, 255, 0.2)"
                iconsCount={5}
                allowFraction={false}
              />
            </div>

            <div style={{ marginBottom: "25px" }}>
              <textarea
                style={{
                  width: "100%",
                  height: "100px",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(138, 187, 69, 0.5)",
                  borderRadius: "12px",
                  padding: "12px",
                  color: "#fff",
                  fontSize: "14px",
                  resize: "none",
                  outline: "none",
                }}
                placeholder="Add your Review"
                value={commentVal}
                onChange={(e) => setCommentVal(e.target.value)}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "15px",
                justifyContent: "center",
                marginTop: "10px",
              }}
            >
              <button
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor: "#FF4500",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "14px",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                }}
                onClick={skipRating}
              >
                Skip, Rate Later
              </button>
              <button
                style={{
                  flex: 1,
                  padding: "12px",
                  backgroundColor: "#8ABB45",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "14px",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                }}
                onClick={submitRating}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PrivateVideoRoomScreen;
